import React, { useState, useCallback, createContext, useContext } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, X } from 'lucide-react';

/**
 * ConfirmDialog + useConfirm hook
 * ========================================
 * Native browser `confirm()` is blocked inside cross-origin iframes (e.g. our
 * Emergent preview iframe wrapper) and bubbles in unexpected ways through
 * modals. This is a small in-app replacement.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete?', body: '...', confirmText: 'Delete' })) {
 *     // user confirmed
 *   }
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {opts, resolve}

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({ opts, resolve });
    });
  }, []);

  const close = (result) => {
    if (state?.resolve) state.resolve(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => close(false)}
          data-testid="confirm-dialog-backdrop"
        >
          <div
            className="w-full max-w-sm bg-card border border-border rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-dialog"
          >
            <div className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-rose-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold">{state.opts.title || 'Are you sure?'}</h3>
                  {state.opts.body && (
                    <p className="text-xs text-muted-foreground mt-1">{state.opts.body}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="confirm-dialog-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => close(false)}
                  data-testid="confirm-dialog-cancel"
                >
                  {state.opts.cancelText || 'Cancel'}
                </Button>
                <Button
                  size="sm"
                  className={state.opts.destructive !== false ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
                  onClick={() => close(true)}
                  data-testid="confirm-dialog-confirm"
                >
                  {state.opts.confirmText || 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // graceful fallback when provider is missing — falls back to window.confirm
    return (opts) => Promise.resolve(window.confirm(opts?.body || opts?.title || 'Are you sure?'));
  }
  return ctx;
}
